import React from 'react'
import styled from 'styled-components'
import { Flex, Box } from 'reflexbox/styled-components'
import PropTypes from 'prop-types'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'

import { H3 } from 'lib/ui/primitives'

import IconCheck from '@anvilco-pkg/ui/components/Icon/IconCheck'
import {
  PRICING_PLANS,
  PRODUCT_PDF_SERVICES,
  PRODUCT_WORKFLOWS,
  PRODUCT_E_SIGN,
  PRODUCT_WEBFORMS,
  PRODUCT_DOCUMENT_AI,
} from '@anvilco-pkg/sanity'

import useAnvilUser from 'hooks/useAnvilUser'

import VideoModal from 'components/VideoModal'
import SubSection from 'components/page/Section/SubSection'
import Button from 'components/Button'
import UniversalLink from 'components/UniversalLink'
import PlayButton from 'components/PlayButton'

import media from 'media'
import theme from 'theme'

import { APP_URL } from 'helpers'
import { getProductSlug } from 'helpers/path'
import { isFeatureNew } from 'helpers/feature'

const HeroContent = styled(Flex).attrs({
  flexDirection: ['column', 'column', 'column', 'row'],
})`
  gap: 24px;

  ${media.giant`
    gap: 50px;
  `}

  ${media.beyondGiant`
    gap: 110px;
  `}
`

const TextContainer = styled(Box).attrs({
  maxWidth: ['100%', '100%', '100%', 365],
})``

const AssetContainer = styled(Box).attrs({ flex: 1 })``

const Title = styled(H3).attrs({ as: 'h1', mb: 16 })``

const Description = styled(Box).attrs({ mb: 32 })``

const CtaWrapper = styled(Box).attrs({ mb: 12 })``

const Cta = styled(Button)``

const NewBadge = styled(Box).attrs({
  px: '10px',
  py: '3px',
  mb: [8, 8, 8, 16, 24],
})`
  border: 1px solid ${theme.colors.inputBorderColor};
  border-radius: 100px;
  font-size: 12px;
  font-weight: ${theme.fontWeights.normal};
  text-transform: uppercase;
  line-height: 1.5;
  background-color: ${theme.colors.white};
  display: inline-flex;
`

const VideoAssetContainer = styled(Box).attrs({
  height: ['auto', 'auto', 'auto', 355],
})`
  position: relative;
  cursor: pointer;
  width: 100%;
`

const VideoAssetInner = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
`

const StyledVideoAssetPlaceholderImg = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 1;
`

const PlansContainer = styled(Flex).attrs({
  mt: [10, 20],
  flexDirection: ['column', 'row'],
  justifyContent: ['flex-start', 'flex-start', 'flex-end'],
})`
  gap: 9px;
`

const PlanText = styled(Box).attrs({ fontSize: 15 })``

const PlanOptions = styled(Flex).attrs({ alignItems: 'center' })`
  gap: 9px;
`

const PlanContainer = styled(Flex)
  .withConfig({
    shouldForwardProp: (prop) => ['isActive'].indexOf(prop) === -1,
  })
  .attrs({
    fontSize: 12,
    fontWeight: theme.fontWeights.bold,
    height: 24,
    alignItems: 'center',
    px: '10px',
  })`
    background-color: ${({ isActive }) => (isActive ? theme.colors.white : 'transparent')};
    border: 1px solid ${({ isActive }) => (isActive ? theme.colors.black : theme.colors.blacks[40])};
    color: ${({ isActive }) => (isActive ? theme.colors.black : theme.colors.blacks[40])};
    border-radius: 100px;
  `

const StyledIconCheck = styled(IconCheck)`
  margin-right: 6px;
`

const Plan = ({ isActive, children }) => (
  <PlanContainer isActive={isActive}>
    {isActive && (
      <StyledIconCheck fill={theme.colors.greens[50]} width={8} height={8} />
    )}
    {children}
  </PlanContainer>
)

Plan.propTypes = {
  isActive: PropTypes.bool,
  children: PropTypes.node,
}

const BACKGROUND_COLORS = {
  [getProductSlug(PRODUCT_PDF_SERVICES)]: theme.colors.blues[5],
  [getProductSlug(PRODUCT_WORKFLOWS)]: theme.colors.greens[5],
  [getProductSlug(PRODUCT_E_SIGN)]: theme.colors.yellows[5],
  [getProductSlug(PRODUCT_WEBFORMS)]: theme.colors.greens[5],
  [getProductSlug(PRODUCT_DOCUMENT_AI)]: theme.colors.blues[5],
}

const FeatureHeroSubSection = (props) => {
  const {
    name,
    description,
    coverImage,
    youtubeId,
    pricingPlan,
    docsLink,
    tag,
  } = props
  const { eid } = useAnvilUser() || {}
  const isUserLoggedInToAnvil = !!eid
  const isNew = isFeatureNew(props)

  return (
    <SubSection background={BACKGROUND_COLORS[tag]}>
      <HeroContent>
        <TextContainer>
          {isNew && <NewBadge>🔥 New</NewBadge>}
          <Title>{name}</Title>
          <Description>{description}</Description>
          <CtaWrapper>
            <Cta
              type="black"
              chevron={{ position: 'inner', direction: 'right' }}
              size="large"
              to={
                isUserLoggedInToAnvil ? 'contact-support' : `${APP_URL}/signup`
              }
            >
              {isUserLoggedInToAnvil
                ? `Talk to us about ${name}`
                : 'Sign up to use'}
            </Cta>
          </CtaWrapper>
          {docsLink?.path && (
            <UniversalLink to={docsLink.path}>See docs</UniversalLink>
          )}
        </TextContainer>
        <AssetContainer>
          {youtubeId ? (
            <VideoModal
              title={name}
              description={description}
              videoId={youtubeId}
              renderPlaceholder={({ onClick }) => {
                return (
                  <VideoAssetContainer onClick={onClick}>
                    <VideoAssetInner>
                      <PlayButton />
                      <StyledVideoAssetPlaceholderImg
                        image={getImage(coverImage.asset.gatsbyImageData)}
                        alt={name}
                      />
                    </VideoAssetInner>
                  </VideoAssetContainer>
                )
              }}
            />
          ) : (
            <StyledVideoAssetPlaceholderImg
              image={getImage(coverImage.asset.gatsbyImageData)}
              alt={name}
            />
          )}
          <PlansContainer>
            <PlanText>Available with:</PlanText>
            <PlanOptions>
              {PRICING_PLANS.map((plan) => (
                <Plan isActive={pricingPlan.includes(plan.key)} key={plan.key}>
                  {plan.value} plan
                </Plan>
              ))}
            </PlanOptions>
          </PlansContainer>
        </AssetContainer>
      </HeroContent>
    </SubSection>
  )
}

FeatureHeroSubSection.propTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  docsLink: PropTypes.object,
  coverImage: PropTypes.object,
  youtubeId: PropTypes.string,
  pricingPlan: PropTypes.arrayOf(PropTypes.string),
  newUntil: PropTypes.any,
  tag: PropTypes.string,
}

export default FeatureHeroSubSection
